/**
 * `/audiences` — every list this workspace has uploaded.
 *
 * The one thing this screen must not do is lie about an import that is still
 * running. `datasets.status` moves `pending → processing → ready | failed`
 * behind a job, so the rows that arrive with the page are a snapshot, not the
 * truth: any row still in flight is polled through `useDatasetStatusPolling`,
 * its live counts are merged over the snapshot, and the list is reloaded once
 * the import settles so the rest of the row catches up too.
 *
 * ## The Rows column is four different claims, not one
 *
 * `importedCount` and `rejectedCount` are real numbers in every status, and in
 * three of the four they mean something other than a finished tally. That is
 * the zero-versus-not-measured rule at table scale, so the second line under
 * the count names the state it is reporting from:
 *
 *   pending     nothing has been read at all — no counts, the words instead
 *   processing  the numbers are climbing, so "none rejected **yet**". A bare
 *               "none rejected" beside a running import is a verdict on an
 *               import that has not returned one
 *   failed      the import stopped, so `0 / 1,287` is not a clean sheet and
 *               `rejectedCount === 0` is not a clean bill of health either
 *   ready       the finished report, and the only status where the plain
 *               wording is true
 *
 * ## Why the first-run copy is four lines rather than two paragraphs
 *
 * The long version explained *why* a sheet becomes its own list and what the
 * mapping step will ask — reasoning that belongs to whoever builds the next
 * screen, not to somebody looking at an empty table. It moved to the headers of
 * `UploadPage` and `ColumnMappingForm`, which is where a person actually meets
 * those decisions. What stays is one instruction — mapping comes before any
 * import — short enough to survive a skim, which is the only reading this
 * state gets. The promise that unmapped columns are kept used to stay here as
 * well; slice 29 moved it to the mapping step, where it is made about a real
 * file, and this paragraph said otherwise until slice 66 corrected it.
 *
 * ## Slice 66: the status column is the product's one status mapping
 *
 * `DatasetStatusBadge` draws the four statuses on the mapping every status
 * chip now shares: waiting on you is warn, running is info, finished is good,
 * broken is bad. The visible change on this table is "Awaiting mapping", which
 * was neutral grey and is amber — it is the one row asking for an action, and
 * a list in that state has imported nothing. The table itself is unchanged:
 * the Rows column's four claims above are decided by status, and a restyle had
 * no business near them.
 */
import { DEFAULT_PAGE_SIZE, datasetSortSchema, type DatasetSummary } from '@wend/shared';
import {
  Card,
  CardContent,
  DataTable,
  Input,
  Spinner,
  buttonVariants,
  type DataTableColumn,
  type DataTableSort,
} from '@wend/ui';
import { FileSpreadsheet, Search, Upload, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { DatasetStatusBadge } from '../../components/DatasetStatusBadge';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { listDatasets } from '../../lib/api/datasets';
import { errorMessage } from '../../lib/api/errors';
import { datasetSheetLabel } from '../../lib/dataset-display';
import { formatDate, formatCount, pluralise } from '../../lib/format';
import { useAsyncData } from '../../lib/use-async-data';
import { useDatasetStatusPolling } from '../../lib/use-dataset-status';

/** Typing pauses this long before the list re-queries. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The line under the imported/total count, in the status it is reporting from.
 *
 * `rejectedCount` is zero in three situations that mean three different things,
 * and only one of them is "nothing was rejected". A running import has not
 * finished counting; a failed one never got to the end of the file at all.
 */
function rejectedLine(dataset: DatasetSummary): string {
  const rejected = dataset.rejectedCount;

  if (dataset.status === 'failed') {
    return rejected === 0 ? 'import stopped' : `${formatCount(rejected)} rejected before it stopped`;
  }
  if (dataset.status === 'processing') {
    return rejected === 0 ? 'none rejected yet' : `${formatCount(rejected)} rejected so far`;
  }
  return rejected === 0 ? 'none rejected' : `${formatCount(rejected)} rejected`;
}

export function AudiencesPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'createdAt', direction: 'desc' });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  const sortField = datasetSortSchema.catch('createdAt').parse(sort.key);

  const { data, error, refreshing, loadingFirst, reload } = useAsyncData(
    (signal) =>
      listDatasets(
        {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          sort: sortField,
          direction: sort.direction,
          ...(search === '' ? {} : { search }),
        },
        signal,
      ),
    [page, search, sortField, sort.direction],
  );

  const items = useMemo<DatasetSummary[]>(() => data?.items ?? [], [data]);

  /* -- live status for anything still importing --------------------------- */

  const inFlightIds = useMemo(
    () =>
      items
        .filter((item) => item.status === 'pending' || item.status === 'processing')
        .map((item) => item.id),
    [items],
  );

  // A settled import changes more than the status — the counts are final and
  // the row's summary is stale — so the list is re-read rather than patched.
  const onSettled = useCallback(() => {
    reload();
  }, [reload]);

  const { statuses, error: pollError } = useDatasetStatusPolling(inFlightIds, onSettled);

  /** The snapshot from the list, with anything the poll has since learned. */
  const rows = useMemo<DatasetSummary[]>(
    () =>
      items.map((item) => {
        const live = statuses[item.id];
        if (live === undefined) return item;
        return {
          ...item,
          status: live.status,
          rowCount: live.rowCount,
          importedCount: live.importedCount,
          rejectedCount: live.rejectedCount,
        };
      }),
    [items, statuses],
  );

  const columns = useMemo<Array<DataTableColumn<DatasetSummary>>>(
    () => [
      {
        key: 'name',
        header: 'List',
        sortable: true,
        cell: (dataset) => {
          // One workbook produces one list per worksheet, and they share a file
          // name. Without the sheet beside it, two rows of the same upload read
          // as duplicates of each other.
          const sheet = datasetSheetLabel(dataset);

          return (
            <div className="min-w-0">
              <Link
                to={`/audiences/${dataset.id}`}
                className="font-medium text-text underline-offset-4 hover:underline"
              >
                {dataset.name}
              </Link>
              <p className="flex min-w-0 items-center gap-1.5 font-mono text-[0.72rem] tracking-[0.04em] text-muted">
                <span className="truncate">{dataset.sourceFilename}</span>
                {sheet !== null && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="flex min-w-0 items-center gap-1 text-muted">
                      <FileSpreadsheet aria-hidden="true" className="size-3 shrink-0" />
                      <span className="sr-only">worksheet </span>
                      <span className="truncate">{sheet}</span>
                    </span>
                  </>
                )}
              </p>
            </div>
          );
        },
      },
      {
        key: 'rows',
        header: 'Rows',
        align: 'right',
        width: '11rem',
        cell: (dataset) => {
          if (dataset.status === 'pending') {
            return <span className="text-muted">Not imported</span>;
          }
          return (
            <div>
              <p className="font-mono text-[0.85rem] tabular-nums text-text">
                {formatCount(dataset.importedCount)}
                <span className="text-muted"> / {formatCount(dataset.rowCount)}</span>
              </p>
              <p className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                {rejectedLine(dataset)}
              </p>
            </div>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        width: '11rem',
        cell: (dataset) => <DatasetStatusBadge status={dataset.status} />,
      },
      {
        key: 'createdAt',
        header: 'Uploaded',
        sortable: true,
        width: '10rem',
        cell: (dataset) => (
          <span className="whitespace-nowrap text-muted">{formatDate(dataset.createdAt)}</span>
        ),
      },
    ],
    [],
  );

  const pagination = data?.pagination ?? null;
  const failedToLoad = error !== null && data === null;

  return (
    <AppLayout
      title="Audiences"
      actions={
        <Link to="/audiences/upload" className={buttonVariants({ size: 'sm' })}>
          <Upload aria-hidden="true" className="size-4" />
          Upload a list
        </Link>
      }
    >
      <div className="flex flex-col gap-5">
        {pollError !== null && (
          <FormAlert tone="error">
            {errorMessage(pollError)} Live import progress has stopped updating — reload the page to
            pick it back up.
          </FormAlert>
        )}

        {failedToLoad ? (
          <Card>
            <ErrorState error={error} title="Could not load your lists" onRetry={reload} />
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-0 flex-1 sm:max-w-xs">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
                />
                <Input
                  type="search"
                  className="pl-9"
                  placeholder="Search by list or file name"
                  aria-label="Search uploaded lists"
                  value={searchInput}
                  onChange={(event) => {
                    setSearchInput(event.target.value);
                  }}
                />
              </div>

              {refreshing && <Spinner size="sm" label="Updating the list" />}

              {pagination !== null && pagination.total > 0 && (
                <p className="ml-auto font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                  {pluralise(pagination.total, 'list')}
                </p>
              )}
            </div>

            {error !== null && (
              <FormAlert tone="error">
                {errorMessage(error)} The list below is the last version that loaded.
              </FormAlert>
            )}

            <DataTable
              label="Uploaded lists"
              columns={columns}
              rows={rows}
              getRowKey={(dataset) => dataset.id}
              loading={loadingFirst}
              sort={sort}
              onSortChange={setSort}
              minWidth="46rem"
              empty={search === '' ? <FirstUpload /> : <NoMatch search={search} />}
              {...(pagination === null
                ? {}
                : {
                    pagination: {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onPageChange: setPage,
                    },
                  })}
            />
          </>
        )}
      </div>
    </AppLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty states                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The first-run state: what this is, why it is empty, and the one action that
 * fills it — because "No rows to show" in a workspace that has never had rows
 * is not information.
 *
 * This comment used to defend a second line here — "kept, not dropped", the
 * promise about unmapped columns — as the one sentence that must never be
 * shortened away. Slice 29 moved that promise to the mapping step, where it
 * is true of a real file (the block comment inside says why), and the defence
 * outlived the sentence it defended. Rewritten in slice 66 so it cannot argue
 * the next reader into putting the line back: `verify-list-building` now holds
 * that the promise is NOT made here.
 */
function FirstUpload() {
  return (
    <CardContent className="flex flex-col items-center gap-4 px-6 py-12 text-center">
      <span className="grid size-12 place-items-center rounded-base border border-line bg-white/[0.04] text-muted">
        <Users aria-hidden="true" className="size-5" />
      </span>

      <div className="flex max-w-[34rem] flex-col gap-2">
        <h2 className="text-[1.1rem] font-semibold tracking-[-0.022em]">No lists yet</h2>
        <p className="text-[0.92rem] leading-relaxed text-muted">
          Upload a spreadsheet. You map its columns before anything is imported.
        </p>
        {/*
          SLICE 29: THE PROMISE IS KEPT WHERE IT IS TRUE, NOT WHERE IT IS GUESSED.

          *"Columns you do not map are kept, not dropped. Rows that cannot be
          imported are reported with the reason."* is made verbatim five times
          across this route — here, twice in `ColumnMappingForm`, and twice on
          `DatasetDetailPage`. This is the copy made to somebody who has not
          chosen a file, cannot act on it, and will meet it again on the screen
          where it is actually happening.

          A promise repeated five times is not reassurance; it is the reason a
          reader stops reading any of them. It survives once, on the mapping form
          beside the columns it is about, and once on the rejected-row report
          where it is demonstrated rather than asserted.
        */}
      </div>

      <Link to="/audiences/upload" className={buttonVariants({ size: 'sm' })}>
        <Upload aria-hidden="true" className="size-4" />
        Upload your first list
      </Link>
    </CardContent>
  );
}

function NoMatch({ search }: { search: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-4">
      <p className="text-[0.92rem] text-muted">
        No list matches <span className="text-text">“{search}”</span>.
      </p>
      <p className="text-[0.85rem] text-muted">Searching matches the list name and the file name.</p>
    </div>
  );
}
